import { Reducer } from 'redux';
import { LOCATION_CHANGE } from 'connected-react-router';
import keyWrapper from '@bufferapp/keywrapper';
import { actionTypes as dataFetchActionTypes } from '@bufferapp/async-data-fetch';
import { actionTypes as queueActionTypes } from '~/queue/reducer';
import { actionTypes as draftActionTypes } from '~/drafts/reducer';
import {
  campaignParser,
  campaignItemParser,
} from '~/server/parsers';
import { getParams, campaignTab } from '~/routes';
import { RpcUpdate } from '~/post/types';
import { Campaign, CampaignTab, CtaLocation } from './types';

export const actionTypes = keyWrapper('CAMPAIGN_VIEW', {
  FETCH_CAMPAIGN: 0,
  PUSHER_CAMPAIGN_CREATED: 0,
  PUSHER_CAMPAIGN_DELETED: 0,
  PUSHER_CAMPAIGN_UPDATED: 0,
  OPEN_COMPOSER: 0,
  CLOSE_COMPOSER: 0,
  GO_TO_ANALYZE_REPORT: 0,
  POST_CONFIRMED_DELETE: 0,
  POST_SHARE_NOW: 0,
  CREATE_POST: 0,
  CREATE_DRAFT: 0,
});

type InitialState =
  | {
      campaign: Campaign;
      campaignPosts: RpcUpdate[] | [];
      isLoading: boolean;
      hideSkeletonHeader: boolean;
      campaignId?: string;
      showComposer: boolean;
      editMode: boolean;
      draftMode: boolean;
      editingPostId?: string;
      selectedProfileId?: string;
      page?: CampaignTab;
    }
  | {
      campaign: undefined;
      campaignPosts: [];
      isLoading: boolean;
      hideSkeletonHeader: boolean;
      campaignId?: string;
      showComposer: boolean;
      editMode: boolean;
      draftMode: boolean;
      editingPostId?: string;
      selectedProfileId?: string;
      page?: CampaignTab;
    };

export const initialState: InitialState = {
  campaign: undefined,
  campaignPosts: [],
  isLoading: true,
  hideSkeletonHeader: false,
  campaignId: undefined,
  showComposer: false,
  editMode: false,
  draftMode: false,
  editingPostId: undefined,
  selectedProfileId: undefined,
  page: undefined,
};

const postReducer = ({ campaignPosts, action, newState }) => {
  return campaignPosts.map((campaign) => {
    if (campaign?.id === action.updateId) {
      return {
        ...campaign,
        ...newState,
      };
    }
    return campaign;
  });
};

const reducer: Reducer<InitialState> = (state = initialState, action) => {
  switch (action.type) {
    case LOCATION_CHANGE: {
      const { pathname } = action.payload.location;
      const params: { id?: string; tab?: CampaignTab } | null = getParams({
        pathname,
        route: campaignTab.route,
      });
      const newCampaignId = params?.id;
      const currentCampaignId = state?.campaign?.id;
      // Not showing a header loader when the campaign stored in the state is the same.
      const isFirstTimeLoading = currentCampaignId !== newCampaignId;
      return {
        ...state,
        campaignId: newCampaignId,
        isLoading: true,
        hideSkeletonHeader: !isFirstTimeLoading,
        page: params?.tab,
      };
    }
    case `getCampaign_${dataFetchActionTypes.FETCH_FAIL}`: {
      return {
        ...state,
        isLoading: false,
        hideSkeletonHeader: false,
      };
    }
    case actionTypes.PUSHER_CAMPAIGN_UPDATED: {
      const { campaign } = state;
      const updatedCampaign: Campaign = campaignParser(action.campaign);

      let campaignToLoad = updatedCampaign;
      if (campaign) {
        campaignToLoad =
          updatedCampaign.id === campaign.id ? updatedCampaign : campaign;
      }

      const channels = updatedCampaign.channels || campaign?.channels;

      return {
        ...state,
        campaign: {
          ...campaignToLoad,
          channels,
        },
      };
    }
    case `getCampaign_${dataFetchActionTypes.FETCH_SUCCESS}`: {
      const { fullItems } = action.args;
      const { items, ...campaign } = action.result;
      return {
        ...state,
        campaign,
        campaignId: campaign.id,
        campaignPosts: (fullItems && items) || [],
        isLoading: false,
        hideSkeletonHeader: false,
      };
    }

    case actionTypes.CREATE_POST:
    case actionTypes.CREATE_DRAFT: {
      const isDraft = action.type === actionTypes.CREATE_DRAFT;
      return {
        ...state,
        showComposer: true,
        editMode: false,
        draftMode: isDraft,
      };
    }
    case actionTypes.OPEN_COMPOSER: {
      return {
        ...state,
        showComposer: true,
        editMode: action.editMode || false,
        editingPostId: action.updateId || undefined,
        selectedProfileId: action.profileId || undefined,
        draftMode: action.draftMode || false,
      };
    }
    case actionTypes.CLOSE_COMPOSER: {
      return {
        ...state,
        showComposer: false,
        editMode: false,
        draftMode: false,
      };
    }
    // Pusher events
    case draftActionTypes.DRAFT_UPDATED:
    case queueActionTypes.POST_UPDATED: {
      const inSentPage = state.page === CampaignTab.Sent;
      const postCampaignId =
        action?.post?.campaignDetails?.id || action?.post?.campaignId;
      const { campaign } = state;
      if (
        campaign &&
        postCampaignId === campaign.id &&
        typeof postCampaignId === 'string' &&
        !inSentPage
      ) {
        if (action.post.isDraft) {
          const newCampaignPosts = state?.campaignPosts?.filter(
            (post) => post.id !== action?.post?.id
          );
          return {
            ...state,
            campaignPosts: newCampaignPosts,
          };
        }
        const newCampaignPosts = state?.campaignPosts?.map((post) => {
          if (post.id === action.post.id) {
            const parsedItem = campaignItemParser(
              {
                content: action.post,
                type: 'update',
              },
              campaign.channels,
              true
            );
            return parsedItem;
          }
          return post;
        });
        return {
          ...state,
          campaignPosts: newCampaignPosts,
        };
      }
      return state;
    }
    case queueActionTypes.POST_CREATED:
    case draftActionTypes.DRAFT_MOVED:
    case draftActionTypes.DRAFT_APPROVED: {
      const inScheduledPage = state.page === CampaignTab.Scheduled;
      const inDraftsOrApprovalPage =
        state.page === CampaignTab.Drafts ||
        state.page === CampaignTab.Approvals;
      const postCampaignId = action?.post?.campaignDetails?.id;
      if (
        postCampaignId === state.campaign?.id &&
        typeof postCampaignId === 'string' &&
        inScheduledPage
      ) {
        const parsedItem = campaignItemParser(
          {
            content: action.post || action.draft,
            type: 'update',
          },
          state?.campaign?.channels,
          true
        );
        return {
          ...state,
          campaignPosts: [...state?.campaignPosts, parsedItem],
        };
      }
      if (inDraftsOrApprovalPage) {
        const newCampaignPosts = state?.campaignPosts?.filter(
          (post) => post.id !== action?.draft?.id
        );
        return {
          ...state,
          campaignPosts: newCampaignPosts,
        };
      }
      return state;
    }
    case queueActionTypes.POST_DELETED: {
      const inScheduledPage = state.page === CampaignTab.Scheduled;
      const postCampaignId = action?.post?.campaignDetails?.id;
      if (
        postCampaignId === state.campaign?.id &&
        typeof postCampaignId === 'string' &&
        inScheduledPage
      ) {
        const newCampaignPosts = state?.campaignPosts?.filter(
          (post) => post.id !== action.post.id
        );
        return {
          ...state,
          campaignPosts: newCampaignPosts,
        };
      }
      return state;
    }
    case queueActionTypes.POST_SENT: {
      const inScheduledPage = state.page === CampaignTab.Scheduled;
      const inSentPage = state.page === CampaignTab.Sent;
      const postCampaignId = action?.post?.campaignDetails?.id;
      const currentCampaign = state?.campaign;
      if (
        currentCampaign &&
        postCampaignId === currentCampaign.id &&
        typeof postCampaignId === 'string' &&
        (inScheduledPage || inSentPage)
      ) {
        const campaignPostsFiltered = state.campaignPosts.filter(
          (post) => post.id !== action.post.id
        );
        const parsedItem = campaignItemParser(
          {
            content: action.post,
            type: 'update',
          },
          state?.campaign?.channels,
          true
        );
        const newCampaignPosts = () => {
          if (inScheduledPage) return campaignPostsFiltered;
          if (inSentPage) return [...state.campaignPosts, parsedItem];
        };
        const { scheduled, sent } = currentCampaign;
        return {
          ...state,
          campaign: {
            ...state.campaign,
            scheduled: scheduled - 1,
            sent: sent + 1,
          },
          campaignPosts: newCampaignPosts(),
        };
      }
      return state;
    }
    // Post events
    case actionTypes.POST_CONFIRMED_DELETE:
    case actionTypes.POST_SHARE_NOW:
    case `sharePostNow_${dataFetchActionTypes.FETCH_FAIL}`:
    case `deletePost_${dataFetchActionTypes.FETCH_FAIL}`: {
      let newState = {};
      switch (action.type) {
        case actionTypes.POST_CONFIRMED_DELETE:
          newState = {
            isConfirmingDelete: false,
            isDeleting: true,
          };
          break;
        case `deletePost_${dataFetchActionTypes.FETCH_FAIL}`:
          newState = {
            isDeleting: false,
          };
          break;
        case actionTypes.POST_SHARE_NOW:
          newState = {
            isWorking: true,
          };
          break;
        case `sharePostNow_${dataFetchActionTypes.FETCH_FAIL}`:
          newState = {
            isWorking: false,
          };
          break;
        default:
      }

      return {
        ...state,
        campaignPosts: postReducer({
          campaignPosts: state.campaignPosts,
          action,
          newState,
        }),
      };
    }
    default:
      return state;
  }
};

export const actions = {
  handleOpenComposer: ({
    post,
    profileId,
    campaignId,
    editMode,
    draftMode = false,
  }: {
    post?: RpcUpdate;
    profileId?: string;
    campaignId: string;
    editMode: boolean;
    draftMode?: boolean;
  }) => ({
    type: actionTypes.OPEN_COMPOSER,
    updateId: post?.id,
    editMode,
    post,
    profileId,
    campaignId,
    draftMode,
  }),
  createPost: (ctaLocation?: CtaLocation) => ({
    type: actionTypes.CREATE_POST,
    ctaLocation,
  }),
  createDraft: (ctaLocation?: CtaLocation) => ({
    type: actionTypes.CREATE_DRAFT,
    ctaLocation,
  }),
  handleCloseComposer: () => ({
    type: actionTypes.CLOSE_COMPOSER,
  }),
  fetchCampaign: ({ campaignId, past, status, fullItems }) => ({
    type: actionTypes.FETCH_CAMPAIGN,
    campaignId,
    past,
    status,
    fullItems,
  }),
  goToAnalyzeReport: (campaign) => ({
    type: actionTypes.GO_TO_ANALYZE_REPORT,
    campaign,
  }),
  handleDeleteConfirmClick: ({ post }) => ({
    type: actionTypes.POST_CONFIRMED_DELETE,
    updateId: post.id,
  }),
  handleShareNowClick: ({ post }) => ({
    type: actionTypes.POST_SHARE_NOW,
    updateId: post.id,
  }),
  handleShareAgainClick: ({ post, profileId }) => {
    return {
      type: actionTypes.OPEN_COMPOSER,
      updateId: post.id,
      editMode: false,
      profileId,
    };
  },
};

export default reducer;
